import { Controller } from "stimulus"
import bb, { line, bar } from "billboard.js"
import { renderTemplate, xhr } from "../utils"

const colorPalette = [ '#bf5257', '#90b4de', '#b5d294', '#e4b871', '#c89ac8' ]
const benchColor = "#ff0000"
const arrayMinValue = array => Math.min(...array)
const numberFormatter = new Intl.NumberFormat(undefined, {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2 })
const formatPercentage = value => numberFormatter.format(value / 100)
const columnTitle = name => (name === "bench" ? "Overall" : name)

const supportedChartTypes = { line, bar }

function groupColorMap(groups) {
  const map = {}
  groups.forEach((group, i) => {
    group.forEach(name => {
      map[name] = colorPalette[i]
    })
  })
  return map
}

export default class extends Controller {
  static targets = [ "chart", "tableContainer", "tableTemplate" ]
  static values = { emptyLabel: String }

  connect() {
    this.createChart()
    xhr(this.data.get("url"))
      .then(r => r.json())
      .then(j => this.updateChart(j))
  }

  createChart() {
    const type = this.data.get("type")
    const options = this.chartOptions(type)
    this.chart = bb.generate(options)
  }

  updateChart(data) {
    if (data.threshold > 0) {
      this.chart.ygrids.add({
        value: data.threshold, text: `${data.threshold}%`, class: 'trend-chart-threshold'
      })
    }

    this.chart.unload()
    this.chart.config("axis.x.tick.rotate", this.tickRotate(data.chart_data))
    this.chart.load({ columns: data.chart_data })
    this.chart.groups(data.groups || [])
    this.chart.data.colors(groupColorMap(data.groups || []))

    if (this.hasTableContainerTarget) {
      this.tableContainerTarget.innerHTML = this.renderTable(data.chart_data)
    }
  }

  renderTable(data) {
    const obj = {
      headers: data[0].splice(1),
      rows: data.splice(1).map(row => {
        return row.slice(0, 1).map(columnTitle).concat(row.slice(1).map(formatPercentage))
      })
    }
    return renderTemplate(this.tableTemplateTarget.innerText, obj)
  }

  tickRotate(data) {
    const [ first, ] = data
    if (first?.length > 7) {
      return -60
    }
  }

  axisType(chartType) {
    return {
      line: "category",
      bar: "category"
    }[chartType]
  }

  chartTicks(type) {
    return {
      bar: [ -100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100 ],
      line: [ -100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100 ]
    }[type]
  }

  chartYMin(data) {
    const minValues = data.slice(1).map(d => arrayMinValue(d.slice(1)))
    return arrayMinValue(minValues)
  }

  chartOptions(type) {
    return {
      bindto: this.chartTarget,
      data: {
        x: 'x',
        columns: [],
        type: supportedChartTypes[type](),
        types: {
          bench: line()
        },
        classes: {
          bench: "bench-line"
        },
        names: {
          bench: columnTitle("bench")
        },
        labels: { format: formatPercentage },
        color: (color, d) => (d === "bench" || d.id === "bench" ? benchColor : color),
        empty: { label: { text: this.emptyLabelValue } }
      },
      axis: {
        x: {
          type: this.axisType(type),
          tick: {
            mode: "fit"
          }
        },
        y: {
          tick: {
            values: this.chartTicks(type),
            format(d) { return `${Math.abs(d)}%` }
          }
        }
      },
      legend: {
        usePoint: true
      },
      grid: {
        y: {
          show: true,
          lines: this.createYLines(type)
        }
      },
      bar: {
        padding: 1,
        radius: 2,
        width: {
          ratio: 0.6
        }
      },
      padding: {
        mode: "fit",
        bottom: 20
      }
    }
  }

  createYLines(chartType) {
    if (chartType == "bar") {
      return [ { value: 0, class: "zero-line" } ]
    }

    return []
  }
}
